import { millisecondsPer } from '@art-suite/art-core-ts';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAuth } from '../../../useAuth';
import { useKaiProApiState } from '../../useKaiProApiState';
import { scanSynchronizerContext } from './ScanSynchronizerContext';
import { SyncedStats, SyncError, SyncLog } from './SyncingTypes';

import { ActiveProgressTracker, createSyncLogger, SetProgressTracker, SyncLogger, SyncLogLevel } from '../SyncLogger';

import { useServerReachabilityCheck } from '../../useServerReachabilityCheck';
import { SyncType } from '../persistence';
import { SyncerProps } from './syncItemBatch';
import { useScanDataSyncer } from './useScanDataSyncer';
import { useScanNotesSyncer } from './useScanNotesSyncer';
import { useScanPhotosSyncer } from './useScanPhotosSyncer';
import { useSyncWorkerWithSubscription } from './useSyncWorkerWithSubscription';

const MAX_ERRORS = 100;
const MAX_LOG_ENTRIES = 1000;
const RE_TRIGGER_INTERVAL_MS = 10 * millisecondsPer.minute;
const MAX_ACTIVE_PROGRESS_UPDATE_RATE_MS = 500;
// Safety net: a paused user might forget. Auto-resume after 12 hours so unsynced data
// eventually makes it to the server even if the app stays open in the background.
const AUTO_RESUME_AFTER_MS = 12 * millisecondsPer.hour;

const normalizeReason = (reason?: any) => {
  if (typeof reason === 'string') return reason;
  try {
    return JSON.stringify(reason);
  } catch (error) {
    return String(reason) || 'unknown';
  }
};

export const ScanSynchronizerProvider = ({
  children,
  logLevel = 'none',
  photoUploadConcurrency,
}: {
  children: ReactNode;
  logLevel?: SyncLogLevel;
  photoUploadConcurrency?: number;
}) => {
  const { token, user, initializing: initializingAuth } = useAuth();
  const checkServerReachability = useServerReachabilityCheck();
  const { isOffline } = useKaiProApiState();
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const togglePaused = useCallback(() => setIsPaused(prev => !prev), []);
  const [stats, setStats] = useState<SyncedStats>({ scanData: 0, scanNote: 0, scanPhoto: 0, scanVideo: 0, errors: [] });
  const activeProgressRef = useRef<ActiveProgressTracker>({});
  const [activeProgressTracker, setActiveProgressTracker] = useState<ActiveProgressTracker>({});
  const flushProgress = useDebouncedCallback(
    () => setActiveProgressTracker({ ...activeProgressRef.current }),
    MAX_ACTIVE_PROGRESS_UPDATE_RATE_MS,
    { leading: true, trailing: true, maxWait: MAX_ACTIVE_PROGRESS_UPDATE_RATE_MS },
  );
  const updateProgress: SetProgressTracker = useCallback(transform => {
    activeProgressRef.current = transform(activeProgressRef.current);
    flushProgress();
  }, [flushProgress]);

  const syncLogRef = useRef<SyncLog[]>([]);
  const [syncLog, setSyncLog] = useState<SyncLog[]>([]);
  const syncLogger: SyncLogger = useMemo(
    () =>
      createSyncLogger(
        logLevel,
        (level: SyncLogLevel, context: string, message: string) => {
          const now = new Date();
          const log = syncLogRef.current;
          log.push({ date: now, context, level, message });

          if (log.length > MAX_LOG_ENTRIES) log.splice(0, log.length - MAX_LOG_ENTRIES);

          setSyncLog([...log]);
        },
        updateProgress,
      ),
    [logLevel],
  );

  // ********************************************************************
  // Running State
  // ********************************************************************
  const errorsRef = useRef<SyncError[]>([]);
  const addError = useCallback((hardError: boolean, context: string, error: any) => {
    if (errorsRef.current.length >= MAX_ERRORS) errorsRef.current.shift();
    errorsRef.current.push({
      date: new Date(),
      hardError,
      context,
      error: error instanceof Error ? error : new Error(String(error)),
    });
    setStats(prev => ({ ...prev, errors: [...errorsRef.current] }));
  }, []);

  const enabled = syncEnabled && !!user && !!token && !isOffline;
  const addSuccess = useCallback((syncType: SyncType, count: number) => {
    setStats(prev => ({ ...prev, [syncType]: (prev[syncType] as number) + count }));
  }, []);

  // ********************************************************************
  // Sync Workers
  // ********************************************************************
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;
  const syncerProps: SyncerProps = { syncLogger, addError, addSuccess, isPausedRef };
  const scanDataSyncer = useScanDataSyncer(syncerProps);
  const scanPhotosSyncer = useScanPhotosSyncer(syncerProps, photoUploadConcurrency);
  const scanNotesSyncer = useScanNotesSyncer(syncerProps);

  const { start: startScanData, isRunning: isRunningScanData } = useSyncWorkerWithSubscription({ enabled, syncer: scanDataSyncer, checkServerReachability });
  const { start: startPhotos, isRunning: isRunningPhotos } = useSyncWorkerWithSubscription({ enabled, syncer: scanPhotosSyncer, checkServerReachability });
  const { start: startNotes, isRunning: isRunningNotes } = useSyncWorkerWithSubscription({ enabled, syncer: scanNotesSyncer, checkServerReachability });
  const isRunning = isRunningScanData || isRunningPhotos || isRunningNotes;

  // ********************************************************************
  // Sync Management Methods
  // ********************************************************************
  const syncEnabledRef = useRef(syncEnabled);
  syncEnabledRef.current = syncEnabled;
  const { startSync, stopSync, clearSyncStats, deleteAndLoseAllUnsyncedData } = useMemo(() => {
    const startSync = (reason?: any) => {
      const normalizedReason = normalizeReason(reason);
      console.log('startSync', normalizedReason, isPausedRef.current ? 'paused' : 'not paused');
      if (isPausedRef.current) {
        syncLogger('info', 'ScanSynchronizer', `Sync paused — ignoring start request ${normalizedReason ? `(${normalizedReason})` : ''}`);
        return;
      }
      if (!syncEnabledRef.current) syncLogger('info', 'ScanSynchronizer', `Starting sync workers ${normalizedReason ? `(${normalizedReason})` : ''}`);
      setSyncEnabled(true);
      startScanData();
      startPhotos();
      startNotes();
    };

    const stopSync = (reason?: any) => {
      const normalizedReason = normalizeReason(reason);
      if (syncEnabledRef.current) {
        syncLogger('info', 'ScanSynchronizer', `Stopping sync workers ${normalizedReason ? `(${normalizedReason})` : ''}`);
      }
      setSyncEnabled(false);
    };

    const clearSyncStats = () => {
      syncLogRef.current = [];
      setSyncLog([]);
      setStats({ scanData: 0, scanNote: 0, scanPhoto: 0, scanVideo: 0, errors: [] });
      activeProgressRef.current = {};
      setActiveProgressTracker({});
      errorsRef.current = [];
    };

    const deleteAndLoseAllUnsyncedData = async () => {
      stopSync('deleteAndLoseAllUnsyncedData');
      await Promise.all([
        scanDataSyncer.model.deleteAndLoseAllUnsyncedData(),
        scanNotesSyncer.model.deleteAndLoseAllUnsyncedData(),
        scanPhotosSyncer.model.deleteAndLoseAllUnsyncedData(),
      ]);
    };

    return { startSync, stopSync, clearSyncStats, deleteAndLoseAllUnsyncedData };
  }, [syncLogger, scanDataSyncer, scanNotesSyncer, scanPhotosSyncer]);

  // ********************************************************************
  // Syncing Triggers
  // ********************************************************************
  // Long Poll: Periodic re-trigger: restart any idle workers that still have pending data
  useEffect(() => {
    const interval = setInterval(() => startSync('long poll'), RE_TRIGGER_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Offline/Online State change
  useEffect(() => isOffline ? stopSync('offline') : startSync('online'), [isOffline]);

  // Pause/Resume: when the user un-pauses, kick off a fresh sync so everything catches up.
  // We don't fire on initial mount (wasPaused starts false), only on the paused → unpaused transition.
  const wasPausedRef = useRef(false);
  useEffect(() => {
    if (!isPaused) startSync('resumed (not paused anymore)');
    else stopSync('paused');
    wasPausedRef.current = isPaused;
  }, [isPaused]);

  // Auto-resume safety net: if the user leaves sync paused for too long, flip it back on.
  // The cleanup clears the pending timeout on manual resume, unmount, or any other dep change,
  // so the timer never fires after the pause has already ended.
  useEffect(() => {
    if (!isPaused) return;
    const timeoutId = setTimeout(() => setIsPaused(false), AUTO_RESUME_AFTER_MS);
    return () => clearTimeout(timeoutId);
  }, [isPaused]);

  // Fresh session, fresh state: whenever the auth token changes (login/logout/refresh),
  // clear any lingering pause so the new session always starts with syncing enabled.
  useEffect(() => {
    setIsPaused(false);
  }, [token]);

  // User State change
  useEffect(() => {
    if (initializingAuth) return;
    if (user) {
      startSync('user');
    } else {
      clearSyncStats();
      stopSync('no user');
    }
  }, [user, initializingAuth]);

  // ********************************************************************
  // Return the Provider with the Context Value
  // ********************************************************************
  return (
    <scanSynchronizerContext.Provider
      value={{
        isRunning,
        stats,
        startSync,
        stopSync,
        clearSyncStats,
        syncLog,
        deleteAndLoseAllUnsyncedData,
        activeProgressTracker,
        isPaused,
        togglePaused,
        syncLogger,
        addSuccess,
        addError,
      }}
    >
      {children}
    </scanSynchronizerContext.Provider>
  );
};
